import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Search, Target, BarChart3, Smartphone, TrendingUp, Users } from 'lucide-react';

const SEAMarketingSection = () => {
  const marketingServices = [
    {
      icon: <Search className="h-12 w-12 text-blue-600" />,
      title: "Google Ads & SEA",
      description: "Professionelle Suchmaschinenmarketing-Kampagnen für maximale Sichtbarkeit und Conversions.",
      features: [
        "Google Shopping Kampagnen",
        "Search & Display Ads",
        "Keyword-Recherche & Optimierung",
        "A/B-Testing von Anzeigen"
      ]
    },
    {
      icon: <Target className="h-12 w-12 text-green-600" />,
      title: "Remarketing & Retargeting",
      description: "Gezielte Ansprache von Website-Besuchern, die noch nicht gekauft haben.",
      features: [
        "Facebook & Instagram Remarketing",
        "Google Display Remarketing",
        "E-Mail-Remarketing-Kampagnen",
        "Dynamische Produktanzeigen"
      ]
    },
    {
      icon: <Smartphone className="h-12 w-12 text-purple-600" />,
      title: "Social Media Marketing",
      description: "Reichweite und Engagement auf Facebook, Instagram, TikTok und anderen Plattformen.",
      features: [
        "Facebook & Instagram Ads",
        "TikTok & Pinterest Marketing",
        "Influencer-Kooperationen",
        "Social Commerce Integration"
      ]
    },
    {
      icon: <BarChart3 className="h-12 w-12 text-orange-600" />,
      title: "Analytics & Tracking",
      description: "Datengetriebene Optimierung für bessere Performance und höhere Conversion-Raten.",
      features: [
        "Google Analytics 4 Setup",
        "Conversion-Tracking",
        "Customer Journey Analyse",
        "ROI-Optimierung"
      ]
    },
    {
      icon: <TrendingUp className="h-12 w-12 text-red-600" />,
      title: "Conversion-Optimierung",
      description: "Systematische Verbesserung der Website für höhere Verkaufsraten.",
      features: [
        "A/B-Testing von Shop-Elementen",
        "Checkout-Optimierung",
        "Mobile-First-Design",
        "Page Speed Optimierung"
      ]
    },
    {
      icon: <Users className="h-12 w-12 text-indigo-600" />,
      title: "Marktplatz-Marketing",
      description: "Optimierung und Bewerbung deiner Produkte auf Amazon, eBay und anderen Marktplätzen.",
      features: [
        "Amazon PPC Kampagnen",
        "eBay Promoted Listings",
        "Produktoptimierung",
        "Cross-Channel-Strategien"
      ]
    }
  ];

  return (
    <section className="py-16 md:py-24 bg-gradient-to-br from-blue-50 to-indigo-50">
      <div className="container">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            <span className="text-blue-600">SEA-Marketing & Online-Werbung</span> für deinen E-Commerce
          </h2>
          <p className="text-lg text-gray-600">
            Der beste Shop nützt nichts, wenn ihn niemand findet. Wir entwickeln nicht nur deine E-Commerce-Plattform, 
            sondern sorgen auch dafür, dass deine Zielgruppe dich online findet und bei dir kauft.
          </p>
        </div>

        {/* Marketing Services */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {marketingServices.map((service, index) => (
            <Card key={index} className="border-0 shadow-lg bg-white h-full hover:shadow-xl transition-all duration-300">
              <CardContent className="p-8 h-full flex flex-col">
                <div className="flex items-center mb-6">
                  {service.icon}
                  <h3 className="text-xl font-bold ml-4">{service.title}</h3>
                </div>
                
                <p className="text-gray-600 mb-6 leading-relaxed flex-grow">
                  {service.description}
                </p>
                
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">Unsere Leistungen:</h4>
                  <ul className="space-y-2">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* B2C Focus */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-xl p-8 text-center text-white">
          <h3 className="text-2xl font-bold mb-4">
            E-Commerce + Marketing = Dein Erfolg
          </h3>
          <p className="text-lg mb-6 opacity-90">
            Wir entwickeln nicht nur deinen Online-Shop, sondern bringen auch die Kunden dazu. 
            Von der technischen Umsetzung bis zur Marketing-Strategie - alles aus einer Hand.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white bg-opacity-20 rounded-lg p-4">
              <h4 className="font-bold mb-1">Technische Entwicklung</h4>
              <p className="text-sm opacity-90">Shopware, Shopify oder Custom</p>
            </div>
            <div className="bg-white bg-opacity-20 rounded-lg p-4">
              <h4 className="font-bold mb-1">Marketing-Integration</h4>
              <p className="text-sm opacity-90">SEA, Social Media, Remarketing</p>
            </div>
            <div className="bg-white bg-opacity-20 rounded-lg p-4">
              <h4 className="font-bold mb-1">Kontinuierliche Betreuung</h4>
              <p className="text-sm opacity-90">Optimierung & Support</p>
            </div>
          </div>
          <a 
            href="#contact" 
            className="inline-flex items-center bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
          >
            E-Commerce + Marketing-Strategie besprechen
          </a>
        </div>
      </div>
    </section>
  );
};

export default SEAMarketingSection; 